import React from 'react';
import { usePOContextSelector, usePODispatch } from './PurchaseOrders.context';
import { RouteComponentProps } from 'react-router';
import { IKitDetail } from 'api/swaggerTypes';

export enum PickSlipAPIType {
    deleteGridLine = 'deleteGridLine',
    updateGridLine = 'updateGridLine',
    updateLine = 'updateLine',
}

export const useTabLocations = (url: string, location: RouteComponentProps['location'], selectedPurchaseOrder: number | string) => {

    const contextDispatch = usePODispatch();
    const selectedContextPurchaseOrder = usePOContextSelector<'PurchaseOrder'>((state) => state.PurchaseOrder);
    const POLineNumber = usePOContextSelector<'LineNumber'>((state) => state.LineNumber);
    const PurchaseOrder = selectedPurchaseOrder || selectedContextPurchaseOrder;

    const tabLocations = React.useMemo(
        () => {

            return ({
                Lines: {
                    pathname: url,
                    search: `?PurchaseOrder=${PurchaseOrder}`
                },
                Delivery: {
                    pathname: `${url.endsWith('/') ? url : `${url}/`}delivery`,
                    search: `?PurchaseOrder=${PurchaseOrder}`
                },
                PurchaseOrderDetails: {
                    pathname: `${url.endsWith('/') ? url : `${url}/`}order-details`,
                    search: `?PurchaseOrder=${PurchaseOrder}`
                },
                LineDetails: {
                    pathname: `${url}/line-details`,
                    search: `?PurchaseOrder=${PurchaseOrder}&LineNumber=${POLineNumber}`
                },
                PurchaseOrdersNotepad: {
                    pathname: `${url.endsWith('/') ? url : `${url}/`}notepad`,
                    search: `?PurchaseOrder=${PurchaseOrder}`
                },
                Commit: {
                    pathname: `${url.endsWith('/') ? url : `${url}/`}commit`,
                    search: `?PurchaseOrder=${PurchaseOrder}`
                },
                PurchaseOrderLineCommitments: {
                    pathname: `${url.endsWith('/') ? url : `${url}/`}line-commitments`,
                    search: `?PurchaseOrder=${PurchaseOrder}`
                },
                PurchaseOrdersDiary: {
                    pathname: `${url.endsWith('/') ? url : `${url}/`}purchase-orders-diary`,
                    search: `?PurchaseOrder=${PurchaseOrder}`
                },
                PurchaseOrdersDiaryDetails: {
                    pathname: `${url.endsWith('/') ? url : `${url}/`}purchase-orders-diary-details`,
                    search: `?PurchaseOrder=${PurchaseOrder}`
                },
            });
        },
        [url, PurchaseOrder, POLineNumber]
    );

    React.useEffect(
        () => {
            const params = (new URLSearchParams(location.search));
            const PurchaseOrderParam = params.get('PurchaseOrder') || '';
            const LineNumberParam = params.get('LineNumber') || '';

            contextDispatch({
                setPurchaseOrder: PurchaseOrderParam,
                setLineNumber: Number(LineNumberParam),
            });
        },
        [contextDispatch]
    );

    return {
        tabLocations
    };
};

type BomApis = 'addLine' | 'calculateFastEntry' | 'updateGridLine' | 'calculateGridLine' | 'update' | 'load';

export const usePriceOverrideAndKitValidations = () => {
    const [multipleBomOpen, setMultipleBomState] = React.useState<boolean>(false);
    const [bomList, setBomList] = React.useState<IKitDetail[]>([]);
    const [kitStatus, setKitStatus] = React.useState<string>('');
    const [selectedBomCode, setSelectedBomCode] = React.useState<string>('');
    const [multipleBomDialogApi, setMultipleBomDialogApi] = React.useState<BomApis>(null);

    const resetStates = () => {
        setMultipleBomState(false);
        setBomList([]);
        setKitStatus('');
        setSelectedBomCode('');
        setMultipleBomDialogApi(null);
    };

    const validate = (response: any) => {
        if (!response.Forms) {
            return;
        }
        const form = response.Forms?.[0];

        if (form?.FormId === 'SelectBomCode') {
            setBomList((response?.KitDetail) ?? [] as any);
            setKitStatus((response?.KitStatus) ?? '');
            setSelectedBomCode((response?.BomCode) ?? '');
            setMultipleBomDialogApi(response?.multipleBomDialogApi ?? null);
            setMultipleBomState(true);
        }
    };

    return {
        bomList,
        kitStatus,
        selectedBomCode,
        multipleBomOpen,
        multipleBomDialogApi,
        resetStates,
        setMultipleBomDialogApi,
        validate,
        setBomList,
        setKitStatus,
        setSelectedBomCode,
        setMultipleBomState
    };
};
